<template>
  <Loader :value="promise" :message="$t('loading', { resource: $tc('lead', 2) }) + '...'" class="page-leads">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #actions>
          <Actions placement="leads" :query="{ filter: { ...filter, references: [id] }, sort }" />
          <DropdownComponent :closeOnClick="false">
            <ButtonComponent :label="$t('add')" :icon="['fal', 'plus']" theme="primary" />
            <template #dropdown="{ close, visible }">
              <ButtonComponent
                tag="router-link"
                :to="{
                  name: 'page-lead-create',
                  params: { reference: id },
                  query: { returnTo: $route.fullPath },
                }"
                :label="$t('add')"
                :icon="['fal', 'plus']"
              />
              <ButtonComponent
                tag="router-link"
                :label="$tc('import')"
                :to="{
                  name: 'import',
                  params: { type: 'lead' },
                  query: { returnTo: $route.path, ref: id, onModel: 'Page' },
                }"
                :icon="['fal', 'file-import']"
                @click="close"
              />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>
      <div class="contacts__filter row between mb-2">
        <FilterComponent
          v-model="filter"
          :default="defaultFilter"
          :options="{
            tags: { items: '/api/contact/tags' },
            search: {
              placeholder: [$t('name'), $t('email')].map((v, i) => (i ? v.toLowerCase() : v)).join(', '),
            },
          }"
          :items="['search', 'tags', 'types']"
        />
        <SortComponent v-model="sort" :items="sortableBy" />
      </div>
      <DataTable
        v-if="items.length"
        sticky="var(--header-height)"
        :selected.sync="selected"
        :sort-by.sync="sort"
        :sortable-by="sortableBy.map(s => s.key)"
        :value="items"
        :columns="[
          { id: 'name', label: $t('name') },
          { id: 'type', label: $tc('type') },
          { id: 'tag', label: $tc('tag', 2) },
          { id: 'leads.0.createdAt', label: $t('createdAt'), align: 'right' },
        ]"
        :row-wrapper="
          lead => ({
            component: 'router-link',
            props: {
              to: {
                name: 'page-lead-edit',
                params: { id: $path('leads.0.id', lead), reference: id },
                query: $route.query,
              },
              style: { color: 'inherit' },
            },
          })
        "
      >
        <template #name="{ item }">
          <ContactCard :value="item" theme="list" />
        </template>
        <template #type="{ item }">
          <Badge :value="$tc(item.type, 1)" :theme="item.type == 'customer' ? 'secondary' : 'default'" />
        </template>
        <template #tag="{ item }">
          <div class="row g-1">
            <Badge v-for="tag in item.tags" :key="tag" :value="tag" />
          </div>
        </template>

        <template v-slot:leads.0.createdAt="{ item }">
          <small class="fade">{{ $path('leads.0.createdAt', item) | Moment('L, LT') }}</small>
        </template>
        <template #select-actions>
          <ButtonComponent :label="$t('sendMessage')" :icon="['fal', 'comment']" @click="showModal = true" />
        </template>
      </DataTable>

      <InfiniteLoading @infinite="next" :identifier="infiniteId">
        <template #spinner>
          <div class="infinite-loading-spinner">
            <span class="infinite-loading-spinner--text">{{ $t('loading', { resource: '...' }) }}</span>
            <FontAwesomeIcon
              class="infinite-loading-spinner--spinner"
              :icon="['fal', 'spinner-third']"
              spin
              size="2x"
            />
          </div>
        </template>
        <template #no-more>{{ $t('noMore', { x: $tc('contact', 2).toLowerCase() }) }}</template>
        <template #no-results>
          <EmptyState
            :title="$t('noX', { x: $tc('lead', 2).toLowerCase() })"
            :icon="['fal', 'address-card']"
            :subtitle="$t('filterEmptyState')"
          />
        </template>
      </InfiniteLoading>
    </div>
    <CommentModal :show.sync="showModal" :promise="commentPromise" @onSuccess="showModal = false" @submit="comment">
      <div class="mb-2">
        <div class="mb-1">{{ $tc('receiver', 2) }}</div>
        <Avatar :value="selected.map(({ name, avatar: src }) => ({ src, name }))" :max="8" />
      </div>
    </CommentModal>
  </Loader>
</template>

<script>
import CommentApi from '@/api/Comment'
import { Debounce } from '@/utils'
import { Moment } from '@/filters'
import Pagination from '@/mixins/Pagination'
import API from '@/api/Page'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import FilterMixin from '@/components/Filter'
import SortComponent, { SortLocal, getSort } from '@/components/Sort'
import Actions from '@/components/Actions'
import Card from '@/components/Card'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import ContactCard from '@/components/Contact/Card'
import CommentModal from '@/components/Comment/Modal'
import Avatar from '@/components/Avatar'
import EmptyState from '@/components/EmptyState'
import InfiniteLoading from 'vue-infinite-loading'

import tracker from '@/tracking'

export default {
  mixins: [FilterMixin({ default: { search: '' } }), Pagination],
  filters: {
    Moment,
  },
  props: {
    id: String,
  },
  watch: {
    filter: {
      handler() {
        this.userUpdate('filter')
      },
      deep: true,
    },
    sort: {
      handler() {
        this.userUpdate('sort')
      },
      deep: true,
    },
  },
  data() {
    return {
      page: {},
      sort: { name: 1 },
      promise: null,
      sortableBy: [
        { key: 'email', label: this.$t('email') },
        { key: 'name', label: this.$t('name') },
        { key: 'leads.0.createdAt', label: this.$t('createdAt'), virtual: true },
      ],
      exportItems: [
        { value: 'name', label: this.$t('name') },
        { value: 'email', label: this.$t('email') },
        { value: 'type', label: this.$tc('type', 1) },
        { value: 'phone', label: this.$t('phone') },
        { value: 'tags', label: this.$tc('tag', 2) },
        { value: 'createdAt', label: this.$t('createdAt') },
      ],
      selected: [],
      showModal: false,
      commentPromise: null,
    }
  },
  computed: {
    breadcrumb() {
      return [
        {
          title: (JSON.parse(sessionStorage.getItem(`page:category`)) || {}).label || this.$tc('page', 2),
          action: { name: 'pages', query: this.$route.query },
        },
        {
          title: this.page.name,
          action: { name: 'page-dashboard', params: { id: this.id }, query: this.$route.query },
        },
        { title: this.$metaInfo.title },
      ]
    },

    items: SortLocal.call(this, 'item.docs'),
  },
  methods: {
    fetch(v) {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()
      if (v) {
        if (Object.values(this[v]).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
          tracker.emit(`PageLeads:${v}`, { ...this[v] })
      }

      this.promise = API.get(
        { id: this.id, filter: this.filter, paginate: this.paginate, sort: getSort.call(this) },

        {
          query: 'PageLeads',
        },
      ).then(res => {
        const { contactsPaginate, ...rest } = res
        this.page = rest

        return contactsPaginate
      })

      return this.promise
    },

    userUpdate: Debounce(function (v) {
      this.reset()
      this.fetch(v)
    }, 200),

    comment(data) {
      tracker.emit('multiple:comment', {})

      this.commentPromise = CommentApi.mutate(
        {
          data: this.selected.map(s => ({
            reference: { onModel: 'Lead', ref: s.leads[0].id },
            ...data,
          })),
        },
        { mutation: 'CommentsCreate', transformPayload: false },
      )
    },
  },
  metaInfo() {
    return {
      title: this.$tc('lead', 2),
    }
  },
  components: {
    Card,
    HeaderComponent,
    SortComponent,
    DropdownComponent,
    Actions,
    DataTable,
    Badge,
    ContactCard,
    CommentModal,
    Avatar,
    EmptyState,
    InfiniteLoading,
  },
}
</script>

<style lang="scss">
.page-leads {
  background-color: $background-grey;
}
</style>
